// #3395 -- the fields of the shared ride-detail block (RideDetailBlock.jsx),
// split out of the old joined strings (horseDetail / pedigree / ownerLine /
// address) so each line can carry its own label and be dropped on its own.
// Pure: orderOfGo.js buildRideRow passes in the entities it already joined, so
// no fetch and no new API field (person.country is FVD tblRider.chrCountry,
// already on the person record).

function clean(value) {
  if (value == null) return "";
  return String(value).trim();
}

function joinParts(parts, sep) {
  return parts.map(clean).filter(Boolean).join(sep);
}

// "12 y/o" for a sane foalYear, "" otherwise (FVD only carries the registry
// fields when HR.hdf has them, so every part is optional).
export function formatAge(foalYear, referenceYear) {
  const fy = Number(foalYear);
  if (!Number.isFinite(fy) || fy <= 0) return "";
  const age = referenceYear - fy;
  return age > 0 && age < 60 ? `${age} y/o` : "";
}

// FVD stores height as text ("16.2h", "17.2", 17.2). Printed as "17.2H".
export function formatHeight(height) {
  const h = clean(height);
  if (!h) return "";
  return /^\d+(\.\d+)?\s*h?$/i.test(h) ? `${h.replace(/\s*h$/i, "")}H` : h;
}

// "Walla Walla, WA - USA". No country: "Walla Walla, WA". The country comes
// off the rider person record; it is never guessed.
export function formatRiderLocation(rider) {
  return joinParts([joinParts([rider?.city, rider?.state], ", "), rider?.country], " - ");
}

// "12 y/o ⋅ Bay ⋅ Hanoverian ⋅ Gelding ⋅ 17.2H", missing parts skipped.
export function formatHorseLine(horse, referenceYear) {
  return joinParts(
    [formatAge(horse?.foalYear, referenceYear), horse?.color, horse?.breed, horse?.sex, formatHeight(horse?.height)],
    " ⋅ ",
  );
}

// A ride whose testId never matched a real test carries an
// "fvd-unmatched:<id>" placeholder (#1855); it is not a real test choice.
function isRealTestId(testId) {
  return typeof testId === "string" && testId.length > 0 && !testId.startsWith("fvd-unmatched:");
}

// Mirrors score-api's classIsTestOfChoice (scoreboard-mode-payload.js, #1643):
// no stored TOC flag exists, a class is a Test of Choice when its rides carry
// more than one distinct REAL testId.
export function classIsTestOfChoice(rides) {
  const realTestIds = new Set((rides || []).map((r) => r?.testId).filter(isRealTestId));
  return realTestIds.size > 1;
}

// Map<classId, boolean> over a ride list, for buildOrderOfGoRows.
export function testOfChoiceByClass(rides) {
  const byClass = new Map();
  for (const ride of rides || []) {
    if (!ride) continue;
    if (!byClass.has(ride.classId)) byClass.set(ride.classId, []);
    byClass.get(ride.classId).push(ride);
  }
  return new Map([...byClass].map(([classId, list]) => [classId, classIsTestOfChoice(list)]));
}

// The block's data. `testName` is only set for a Test of Choice class (the
// class name is never repeated, and a single-test class prints its test in the
// class header already).
export function buildRideDetail({ rider, horse, ownerName, coachName, testName, isTestOfChoice, referenceYear }) {
  return {
    location: formatRiderLocation(rider),
    horseLine: formatHorseLine(horse, referenceYear),
    sire: clean(horse?.sire),
    dam: clean(horse?.dam),
    damsSire: clean(horse?.damsSire),
    owner: clean(ownerName),
    breeder: clean(horse?.breeder),
    // #3396 -- FVD tblEntry.idsCoachID resolved to a person by the caller
    // (ride.coachId); "" when the entry has no coach, so the line is dropped.
    coach: clean(coachName),
    test: isTestOfChoice ? clean(testName) : "",
  };
}
